import type { Binding } from "@/app/types";
import type { Hook, HookPoint, HookWithPlugin, Plugin } from "./types";

/**
 * Group plugins by their binding type
 */
export const groupPluginsByBinding = (plugins: Plugin[]) => ({
  tenant: plugins.filter((p) => p.binding.type === "tenant"),
  project: plugins.filter((p) => p.binding.type === "project"),
});

/**
 * Enrich hooks with their associated plugin data
 */
export const enrichHooksWithPlugins = (
  hooks: Hook[],
  plugins: Plugin[]
): HookWithPlugin[] => {
  const pluginMap = new Map(plugins.map((p) => [p.id, p]));
  return hooks.map((hook) => ({
    ...hook,
    plugin: pluginMap.get(hook.plugin_id),
  }));
};

/**
 * Get binding display info
 */
export const getBindingDisplayInfo = (
  bindingType: Binding
): { label: string; colorClass: string } => {
  switch (bindingType) {
    case "tenant":
      return {
        label: "Organization",
        colorClass: "bg-purple-500/20 text-purple-400",
      };
    case "project":
      return { label: "Project", colorClass: "bg-pink-500/20 text-pink-400" };
    default:
      return {
        label: bindingType,
        colorClass: "bg-muted text-muted-foreground",
      };
  }
};

/**
 * Get health status display info
 */
export const getHealthStatusInfo = (
  status: string
): {
  label: string;
  colorClass: string;
  icon: "check" | "warning" | "error" | "unknown";
} => {
  switch (status) {
    case "healthy":
      return { label: "Healthy", colorClass: "text-green-500", icon: "check" };
    case "degraded":
      return {
        label: "Degraded",
        colorClass: "text-yellow-500",
        icon: "warning",
      };
    case "unhealthy":
      return { label: "Unhealthy", colorClass: "text-red-500", icon: "error" };
    default:
      return {
        label: "Unknown",
        colorClass: "text-muted-foreground",
        icon: "unknown",
      };
  }
};

/**
 * Get configured endpoints from a plugin
 */
export const getConfiguredEndpoints = (plugin: Plugin): HookPoint[] => {
  const endpoints: HookPoint[] = [];
  if (plugin.webhook_config?.endpoints.access) {
    endpoints.push("tool.access");
  }
  if (plugin.webhook_config?.endpoints.pre) {
    endpoints.push("tool.pre");
  }
  if (plugin.webhook_config?.endpoints.post) {
    endpoints.push("tool.post");
  }
  return endpoints;
};

/**
 * Get the phase order for a hook based on binding type and phase.
 * Matches the engine's execution order from cate_design.md:
 *   1. Tenant before - Company-wide policies
 *   2. Project before - Project-specific enrichment
 *   3. Project after - Project-level output filtering
 *   4. Tenant after - Company-wide audit and PII redaction
 */
const getPhaseOrder = (
  bindingType: Binding | undefined,
  phase: "before" | "after"
): number => {
  if (bindingType === "tenant") {
    return phase === "before" ? 1 : 4;
  }
  if (bindingType === "project") {
    return phase === "before" ? 2 : 3;
  }
  return 99; // Unknown binding types go last
};

// Helper type for hooks that may have plugin binding info
type HookMaybeWithPlugin = Hook & { plugin?: { binding?: { type?: Binding } } };

/**
 * Sort hooks by execution order following the CATE design spec:
 * 1. First by phase order (tenant:before → project:before → project:after → tenant:after)
 * 2. Then by priority within each phase (lower = earlier)
 *
 * Works with both Hook[] and HookWithPlugin[]. If binding info is not available
 * (plain Hook), falls back to phase + priority ordering.
 */
export const sortHooksByPriority = <T extends Hook>(hooks: T[]): T[] =>
  [...hooks].sort((a, b) => {
    // Cast to get optional plugin binding info (works for both Hook and HookWithPlugin)
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const aWithPlugin = a as HookMaybeWithPlugin;
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    const bWithPlugin = b as HookMaybeWithPlugin;

    // First sort by phase order (binding + phase determines execution phase)
    const aPhaseOrder = getPhaseOrder(
      aWithPlugin.plugin?.binding?.type,
      a.phase
    );
    const bPhaseOrder = getPhaseOrder(
      bWithPlugin.plugin?.binding?.type,
      b.phase
    );
    if (aPhaseOrder !== bPhaseOrder) {
      return aPhaseOrder - bPhaseOrder;
    }
    // Then by priority within the same phase (lower = earlier)
    return a.priority - b.priority;
  });

/**
 * Get the next available priority for a new hook at a given hook point
 * Priority starts at 0 and increments based on existing hooks of the same binding type
 *
 * @param hooks - Enriched hooks with plugin data
 * @param hookPoint - The hook point to get priority for
 * @param binding - Optional binding type filter (tenant or project). If provided,
 *                  only hooks from plugins with matching binding are considered.
 */
const getNextPriority = (
  hooks: HookWithPlugin[],
  hookPoint: HookPoint,
  binding?: Binding
): number => {
  let hooksAtPoint = hooks.filter((h) => h.hook_point === hookPoint);

  // If binding is specified, filter to only hooks from plugins with matching binding
  if (binding) {
    hooksAtPoint = hooksAtPoint.filter(
      (h) => h.plugin?.binding.type === binding
    );
  }

  if (hooksAtPoint.length === 0) {
    return 0; // Start at 0
  }
  const maxPriority = Math.max(...hooksAtPoint.map((h) => h.priority));
  return Math.min(maxPriority + 1, 1000); // Increment by 1, max 1000
};

/**
 * Get default priorities for all hook points based on existing hooks
 *
 * @param hooks - Enriched hooks with plugin data
 * @param binding - Optional binding type filter. If provided, only considers
 *                  hooks from plugins with matching binding when calculating next priority.
 */
export const getDefaultPriorities = (
  hooks: HookWithPlugin[],
  binding?: Binding
): Record<HookPoint, number> => ({
  "tool.access": getNextPriority(hooks, "tool.access", binding),
  "tool.pre": getNextPriority(hooks, "tool.pre", binding),
  "tool.post": getNextPriority(hooks, "tool.post", binding),
});
